<template>
  <button
    v-show="showButton"
    class="cky-banner-element"
    :class="{
      'btn -anchor-link': location === 'footer',
      'btn -main': location === 'account-data',
      'btn -dark': location === 'embed',
    }"
  >
    Cookie Preferences
  </button>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import { mapActions, mapState } from 'pinia'
import { useUserAuthStore } from '@/stores/user-auth'
import { fbApi } from '@/utils/http-api'

/**
 * Button to open the CookieYes.com cookie preferences modal
 * - only will show to EU users (if banner was shown, cookieyes does geoip check)
 * - will be hidden to US users (even if enabled, doesn't work due to geoip)
 *
 * Listens to cookieyes_consent_update, which is fired for ALL users regardless
 * of consent.
 * - US users: no banner shown, so fires instantly
 */
export default defineComponent({
  name: 'CookieConsentButton',
  components: {},
  props: {
    location: {
      type: String,
      default: 'footer',
      validator: (val: string) => ['footer', 'account-data', 'embed'].includes(val),
    },
    showButtonAlways: {
      type: Boolean,
      default: false,
    },
    // turn off if you have two cookie-consent-button components on the same page
    // as it would invoke the handler for 'cookieyes_consent_update' twice
    handleUpdateEvent: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      showButton: false,
      intervalId: 0,
    }
  },
  computed: {
    ...mapState(useUserAuthStore, ['user', 'consents']),
  },
  mounted() {
    this.$nextTick(() => {
      this.shouldShowButton()
    })

    if (this.handleUpdateEvent) {
      document.addEventListener('cookieyes_consent_update', this.userStorageChanged)
    }
  },

  beforeUnmount() {
    // clean up
    if (this.handleUpdateEvent) {
      document.removeEventListener('cookieyes_consent_update', this.userStorageChanged)
    }

    if (this.intervalId) {
      clearInterval(this.intervalId)
    }
  },

  methods: {
    ...mapActions(useUserAuthStore, ['updateConsents']),

    /**
     * Cookieyes may be blocked by adblockers... in this case we tried to do
     * it "the right way". Fallback to enabling necessary and functional
     * cookies so videos load so US users do not get stuck unable to view YouTube embeds.
     */
    cookieYesMissing() {
      this.updateConsents({
        STORAGE_NECESSARY: true,
        STORAGE_FUNCTIONALITY: true,
        STORAGE_ANALYTICS: false,
        STORAGE_ADS: false,
        STORAGE_OTHER: false,
      })
    },

    /**
     * Via cookieyes support ticket 10265
     *
     * Check the _ckyStore object and the _bannerDisplayState key to see if the
     * banner was shown.  Possible values:
     * - "none" (US users)
     * - "banner" (EU users on initial banner action)
     * - "revisit" (EU users, after banner action completed)
     */
    shouldShowButton() {
      // shouldn't happen unless cookieyes.com outage
      if (!window || !window.cookieyes) {
        console.warn('cookieyes is missing, loading defaults')
        this.cookieYesMissing()
        return
      }

      const { cookieyes } = window

      // cookieyes fires a few XHR related to check geoip and settings
      // before _bannerDisplayState is actually set...
      // hacky solution to wait for it to finish
      const maxAttempts = 10
      let attempts = 0

      if (cookieyes._ckyGetFromStore) {
        // update pinia consent settings (if they changed)
        // console.log('_ckyConsentStore', Array.from(cookieyes._ckyConsentStore))
        this.updateConsents({
          STORAGE_NECESSARY: true,
          STORAGE_FUNCTIONALITY: cookieyes._ckyGetFromStore('functional') === 'yes',
          STORAGE_ANALYTICS: cookieyes._ckyGetFromStore('analytics') === 'yes',
          STORAGE_ADS: cookieyes._ckyGetFromStore('advertisement') === 'yes',
          STORAGE_OTHER: cookieyes._ckyGetFromStore('other') === 'yes',
        })
      } else {
        console.warn('cookieyes is loaded but missing _ckyGetFromStore, loading defaults')
        this.cookieYesMissing()
      }

      this.intervalId = setInterval(() => {
        // console.log('attempt', attempts, JSON.parse(JSON.stringify(cookieyes._ckyStore)))
        attempts += 1

        // if cookieyes is blocked or not loaded
        if (!cookieyes._ckyStore) {
          return
        }

        // note that initial state is "none"
        if (['banner', 'revisit'].includes(cookieyes._ckyStore._bannerDisplayState)) {
          this.showButton = true
          clearInterval(this.intervalId)

          return
        }

        // stop trying
        if (maxAttempts === attempts) {
          clearInterval(this.intervalId)
          return
        }
      }, 1000)
    },

    /**
     * Track consent setting changes
     * @see https://www.cookieyes.com/documentation/events-on-cookie-banner-interactions/
     *
     * US Users (_bannerDisplayState === 'none')
     * - fires on initial page load always
     * - user cannot change settings (cookie consent button not shown)
     * - fires on secondary page loads
     * - never logs to db
     *
     * EU Users
     * - does NOT fire on initial load
     * - fires when user changes settings
     * - does NOT fire on secondary page load
     * - logs to db
     */
    async userStorageChanged(consentEvent: any) {
      // shouldn't happen
      if (!window || !window.cookieyes) {
        return
      }

      const { cookieyes } = window
      const { accepted } = consentEvent.detail

      /* US Users */
      // no action, no logs, not tracking and cookies are all opted in
      if (cookieyes._ckyStore._bannerDisplayState === 'none') {
        // console.log('_ckyConsentStore 2', Array.from(cookieyes._ckyConsentStore))

        this.updateConsents({
          STORAGE_NECESSARY: true,
          STORAGE_FUNCTIONALITY: cookieyes._ckyGetFromStore('functional') === 'yes',
          STORAGE_ANALYTICS: cookieyes._ckyGetFromStore('analytics') === 'yes',
          STORAGE_ADS: cookieyes._ckyGetFromStore('advertisement') === 'yes',
          STORAGE_OTHER: cookieyes._ckyGetFromStore('other') === 'yes',
        })
        return
      }

      /* EU Users */
      // show button, regardless if shouldShowButton
      this.showButton = true

      // update pinia
      const consent = {
        STORAGE_NECESSARY: true,
        STORAGE_FUNCTIONALITY: accepted.includes('functional'),
        STORAGE_ANALYTICS: accepted.includes('analytics'),
        STORAGE_ADS: accepted.includes('advertisement'),
        STORAGE_OTHER: accepted.includes('other'),
      }
      this.updateConsents(consent)

      // if not logged in, do not fire XHR
      if (!this.user.isLoggedIn) {
        return
      }

      // get cookie id for log data
      // _ckyConsentStore is an es6 Map data
      let consentId = ''

      if (cookieyes._ckyGetFromStore) {
        consentId = cookieyes._ckyGetFromStore('consentid')
      }
      // end consentId

      const reqBody = {
        consent,

        // log data
        consentId,
        _bannerDisplayState: cookieyes._ckyStore._bannerDisplayState,
      }

      try {
        await fbApi.post('/my/consents/storage', {
          json: reqBody,
          skipErrorModal: true,
        })
      } catch (err) {
        console.error(err)
      }
    },
  },
})
</script>
